import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2, Save, SlidersHorizontal } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// Settings → Hours & Booking (V2 Round 4)
//
// Per-clinic appointment configuration. Calls:
//   - get_appointment_settings()       (loads on mount)
//   - update_appointment_settings(...) (partial JSONB on Save)
//   - list_clinic_chairs()             (for walk-in default chair dropdown)
// Doctors come from list_clinic_doctor_records (the public.doctors
// roster, which appointments.doctor_id FKs into). NOT list_clinic_doctors
// — that returns memberships+profiles, a different ID space.
//
// Save sends only fields that changed since the last loaded snapshot
// (dirty diff), so concurrent edits to other settings columns don't get
// clobbered.
// =====================================================================

const DAYS = [
  { key: "monday",    label: "Monday"    },
  { key: "tuesday",   label: "Tuesday"   },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday"  },
  { key: "friday",    label: "Friday"    },
  { key: "saturday",  label: "Saturday"  },
  { key: "sunday",    label: "Sunday"    },
];

const SLOT_INTERVALS = [5, 10, 15, 20, 30, 60];
const CALENDAR_OPS_RISK_LABELS = [
  { key: "no_show_history", label: "No-show history" },
  { key: "unpaid_balance", label: "Unpaid balance" },
  { key: "missing_consent", label: "Missing consent" },
  { key: "incomplete_medical_history", label: "Medical history" },
  { key: "hmo_payment_flag", label: "HMO profile" },
  { key: "unconfirmed", label: "Unconfirmed" },
  { key: "email_bounced", label: "Email bounced" },
  { key: "no_phone", label: "No phone" },
];
const CALENDAR_OPS_QUEUE_ITEMS = [
  { key: "patient_late", label: "Patient late" },
  { key: "patient_waiting_too_long", label: "Patient waiting" },
  { key: "checkout_waiting", label: "Checkout waiting" },
  { key: "doctor_split_across_chairs", label: "Doctor split" },
  { key: "reschedule_pending", label: "Reschedule pending" },
  { key: "email_bounced", label: "Email bounced" },
  { key: "no_phone", label: "No phone" },
  { key: "unconfirmed_tomorrow", label: "Unconfirmed tomorrow" },
];

const DEFAULT_HOURS = {
  monday:    { open: "09:00", close: "18:00", closed: false },
  tuesday:   { open: "09:00", close: "18:00", closed: false },
  wednesday: { open: "09:00", close: "18:00", closed: false },
  thursday:  { open: "09:00", close: "18:00", closed: false },
  friday:    { open: "09:00", close: "18:00", closed: false },
  saturday:  { open: "09:00", close: "16:00", closed: false },
  sunday:    { open: null,    close: null,    closed: true  },
};

const DEFAULT_CALENDAR_OPS_SETTINGS = {
  risk_labels_enabled: Object.fromEntries(CALENDAR_OPS_RISK_LABELS.map((item) => [item.key, true])),
  queue_item_types_enabled: Object.fromEntries(CALENDAR_OPS_QUEUE_ITEMS.map((item) => [item.key, true])),
  queue_priority: CALENDAR_OPS_QUEUE_ITEMS.map((item) => item.key),
  thresholds: {
    late_minutes: 10,
    waiting_too_long_minutes: 15,
    checkout_waiting_minutes: 10,
    no_show_count: 2,
    no_show_lookback_months: 12,
    unconfirmed_lookahead_hours: 48,
  },
  queue_scope: "active_branch",
};

export default function SettingsAppointments() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [original, setOriginal] = useState(null);
  const [calendarOpsOriginal, setCalendarOpsOriginal] = useState(null);
  const [form, setForm] = useState({
    hours: DEFAULT_HOURS,
    default_duration_minutes: 30,
    slot_interval_minutes: 15,
    advance_booking_days: 90,
    walk_in_allowed: true,
    walk_in_default_chair_id: "",
    walk_in_default_doctor_id: "",
    no_show_grace_minutes: 15,
    buffer_minutes: 0,
    chair_setup_minutes: 15,
    confirmation_email_1_hours_before: 48,
    confirmation_email_2_hours_before: 24,
    call_queue_trigger_hours_before: 24,
  });
  const [calendarOpsForm, setCalendarOpsForm] = useState(DEFAULT_CALENDAR_OPS_SETTINGS);
  const [chairs, setChairs] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [settingsRes, calendarOpsRes, chairsRes, doctorsRes] = await Promise.all([
      supabase.rpc("get_appointment_settings"),
      supabase.rpc("get_calendar_ops_settings"),
      supabase.rpc("list_clinic_chairs"),
      supabase.rpc("list_clinic_doctor_records"),
    ]);
    setLoading(false);
    if (settingsRes.error) {
      setError(settingsRes.error.message ?? "Could not load appointment settings.");
      return;
    }
    if (!chairsRes.error)  setChairs(chairsRes.data ?? []);
    if (!doctorsRes.error) setDoctors(doctorsRes.data ?? []);

    const data = settingsRes.data || {};
    const next = {
      hours:                     mergeHours(data.hours),
      default_duration_minutes:  data.default_duration_minutes  ?? 30,
      slot_interval_minutes:     data.slot_interval_minutes     ?? 15,
      advance_booking_days:      data.advance_booking_days      ?? 90,
      walk_in_allowed:           data.walk_in_allowed           ?? true,
      walk_in_default_chair_id:  data.walk_in_default_chair_id  ?? "",
      walk_in_default_doctor_id: data.walk_in_default_doctor_id ?? "",
      no_show_grace_minutes:     data.no_show_grace_minutes     ?? 15,
      buffer_minutes:            data.buffer_minutes            ?? 0,
      chair_setup_minutes:       data.chair_setup_minutes       ?? 15,
      confirmation_email_1_hours_before: data.confirmation_email_1_hours_before ?? 48,
      confirmation_email_2_hours_before: data.confirmation_email_2_hours_before ?? 24,
      call_queue_trigger_hours_before:   data.call_queue_trigger_hours_before   ?? 24,
    };
    setForm(next);
    setOriginal(next);
    const nextCalendarOps = normalizeCalendarOpsSettings(calendarOpsRes.data);
    setCalendarOpsForm(nextCalendarOps);
    setCalendarOpsOriginal(nextCalendarOps);
  }, []);

  useEffect(() => { load(); }, [load]);

  const appointmentDirty = useMemo(() => {
    if (!original) return false;
    return JSON.stringify(form) !== JSON.stringify(original);
  }, [form, original]);
  const calendarOpsDirty = useMemo(() => {
    if (!calendarOpsOriginal) return false;
    return JSON.stringify(calendarOpsForm) !== JSON.stringify(calendarOpsOriginal);
  }, [calendarOpsForm, calendarOpsOriginal]);
  const dirty = appointmentDirty || calendarOpsDirty;

  function setHourField(dayKey, field, value) {
    setForm((f) => ({
      ...f,
      hours: { ...f.hours, [dayKey]: { ...f.hours[dayKey], [field]: value } },
    }));
  }

  function setClosed(dayKey, closed) {
    setForm((f) => ({
      ...f,
      hours: {
        ...f.hours,
        [dayKey]: closed
          ? { open: null, close: null, closed: true }
          : { open: f.hours[dayKey]?.open || "09:00", close: f.hours[dayKey]?.close || "18:00", closed: false },
      },
    }));
  }

  function setLunchEnabled(dayKey, enabled) {
    setForm((f) => {
      const day = f.hours[dayKey] || {};
      const next = { ...day };
      if (enabled) {
        next.lunch = next.lunch?.start && next.lunch?.end
          ? next.lunch
          : { start: "12:00", end: "13:00" };
      } else {
        delete next.lunch;
      }
      return { ...f, hours: { ...f.hours, [dayKey]: next } };
    });
  }

  function setLunchField(dayKey, field, value) {
    setForm((f) => {
      const day = f.hours[dayKey] || {};
      const lunch = { ...(day.lunch || { start: "12:00", end: "13:00" }), [field]: value };
      return { ...f, hours: { ...f.hours, [dayKey]: { ...day, lunch } } };
    });
  }

  async function handleSave() {
    setSaving(true); setError(null);
    // Build a diff payload — only send fields that changed.
    const payload = {};
    if (appointmentDirty) {
      for (const k of Object.keys(form)) {
        if (JSON.stringify(form[k]) !== JSON.stringify(original?.[k])) {
          payload[k] = form[k] === "" ? null : form[k];
        }
      }
    }
    const appointmentResult = appointmentDirty && Object.keys(payload).length > 0
      ? await supabase.rpc("update_appointment_settings", { p_input: payload })
      : { data: null, error: null };
    const calendarOpsResult = calendarOpsDirty
      ? await supabase.rpc("update_calendar_ops_settings", { p_settings: calendarOpsForm })
      : { data: null, error: null };
    setSaving(false);
    const e = appointmentResult.error || calendarOpsResult.error;
    if (e) {
      setError(e.message ?? "Could not save.");
      toast.error(e.message ?? "Could not save.");
      return;
    }
    const data = appointmentResult.data;
    const next = {
      hours:                     mergeHours(data?.hours),
      default_duration_minutes:  data?.default_duration_minutes  ?? form.default_duration_minutes,
      slot_interval_minutes:     data?.slot_interval_minutes     ?? form.slot_interval_minutes,
      advance_booking_days:      data?.advance_booking_days      ?? form.advance_booking_days,
      walk_in_allowed:           data?.walk_in_allowed           ?? form.walk_in_allowed,
      walk_in_default_chair_id:  data?.walk_in_default_chair_id  ?? "",
      walk_in_default_doctor_id: data?.walk_in_default_doctor_id ?? "",
      no_show_grace_minutes:     data?.no_show_grace_minutes     ?? form.no_show_grace_minutes,
      buffer_minutes:            data?.buffer_minutes            ?? form.buffer_minutes,
      chair_setup_minutes:       data?.chair_setup_minutes       ?? form.chair_setup_minutes,
      confirmation_email_1_hours_before: data?.confirmation_email_1_hours_before ?? form.confirmation_email_1_hours_before,
      confirmation_email_2_hours_before: data?.confirmation_email_2_hours_before ?? form.confirmation_email_2_hours_before,
      call_queue_trigger_hours_before:   data?.call_queue_trigger_hours_before   ?? form.call_queue_trigger_hours_before,
    };
    setForm(next);
    setOriginal(next);
    const nextCalendarOps = normalizeCalendarOpsSettings(calendarOpsResult.data || calendarOpsForm);
    setCalendarOpsForm(nextCalendarOps);
    setCalendarOpsOriginal(nextCalendarOps);
    toast.success("Saved.");
  }

  function setCalendarOpsThreshold(key, value) {
    setCalendarOpsForm((current) => ({
      ...current,
      thresholds: {
        ...current.thresholds,
        [key]: Math.max(1, Number(value) || 1),
      },
    }));
  }

  function setCalendarOpsRisk(key, checked) {
    setCalendarOpsForm((current) => ({
      ...current,
      risk_labels_enabled: {
        ...current.risk_labels_enabled,
        [key]: checked,
      },
    }));
  }

  function setCalendarOpsQueueItem(key, checked) {
    setCalendarOpsForm((current) => ({
      ...current,
      queue_item_types_enabled: {
        ...current.queue_item_types_enabled,
        [key]: checked,
      },
    }));
  }

  function setCalendarOpsPriority(index, key) {
    setCalendarOpsForm((current) => {
      const next = [...current.queue_priority];
      next[index] = key;
      return { ...current, queue_priority: next };
    });
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-appointments" data-tutorial-id="settings-appointments">
      {/* Header bar with Save */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Hours & Booking</h1>
          <p className="text-xs text-slate-500">Operating hours and the rules the calendar follows.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="settings-appointments-save"
          data-tutorial-id="settings-appointments-save"
          className="px-4 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>

      {error ? (
        <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      ) : null}

      {/* Clinic hours */}
      <Card title="Clinic hours" subtitle="Used by the calendar grid, walk-in defaults, and chair-utilization math." tutorialId="settings-appointments-hours">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase text-slate-500 tracking-wider">
                <th className="text-left font-semibold pb-2 pr-4">Day</th>
                <th className="text-left font-semibold pb-2 pr-4">Open</th>
                <th className="text-left font-semibold pb-2 pr-4">Close</th>
                <th className="text-left font-semibold pb-2 pr-4">Closed</th>
                <th className="text-left font-semibold pb-2 pr-4">Lunch break</th>
                <th className="text-left font-semibold pb-2 pr-4">Lunch start</th>
                <th className="text-left font-semibold pb-2">Lunch end</th>
              </tr>
            </thead>
            <tbody>
              {DAYS.map(({ key, label }) => {
                const day = form.hours[key] || { open: null, close: null, closed: true };
                const hasLunch = !!(day.lunch?.start && day.lunch?.end);
                const lunchDisabled = !!day.closed;
                return (
                  <tr key={key} className="border-t border-slate-100">
                    <td className="py-2 pr-4 text-slate-800 font-medium">{label}</td>
                    <td className="py-2 pr-4">
                      <input
                        type="time" value={day.open || ""}
                        onChange={(e) => setHourField(key, "open", e.target.value)}
                        disabled={day.closed}
                        data-testid={`hours-${key}-open`}
                        className="px-2 py-1 text-sm border border-slate-200 rounded disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="time" value={day.close || ""}
                        onChange={(e) => setHourField(key, "close", e.target.value)}
                        disabled={day.closed}
                        data-testid={`hours-${key}-close`}
                        className="px-2 py-1 text-sm border border-slate-200 rounded disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox" checked={!!day.closed}
                        onChange={(e) => setClosed(key, e.target.checked)}
                        data-testid={`hours-${key}-closed`}
                        className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox" checked={hasLunch}
                        onChange={(e) => setLunchEnabled(key, e.target.checked)}
                        disabled={lunchDisabled}
                        data-testid={`hours-${key}-lunch-enabled`}
                        className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300 disabled:opacity-40"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="time" value={day.lunch?.start || ""}
                        onChange={(e) => setLunchField(key, "start", e.target.value)}
                        disabled={lunchDisabled || !hasLunch}
                        data-testid={`hours-${key}-lunch-start`}
                        className="px-2 py-1 text-sm border border-slate-200 rounded disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="time" value={day.lunch?.end || ""}
                        onChange={(e) => setLunchField(key, "end", e.target.value)}
                        disabled={lunchDisabled || !hasLunch}
                        data-testid={`hours-${key}-lunch-end`}
                        className="px-2 py-1 text-sm border border-slate-200 rounded disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-[12px] text-slate-500">
            Lunch break is excluded from chair-utilization capacity. Emergency / walk-in appointments during lunch
            remain bookable — this only changes how the % is calculated.
          </p>
        </div>
      </Card>

      {/* Booking defaults */}
      <Card title="Booking defaults" subtitle="What the booking panel and calendar grid assume." tutorialId="settings-appointments-defaults">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Default duration when procedure has none">
            <select
              value={form.default_duration_minutes}
              onChange={(e) => setForm({ ...form, default_duration_minutes: Number(e.target.value) })}
              data-testid="appt-default-duration"
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
            >
              <option value={15}>15 min</option>
              <option value={20}>20 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>60 min</option>
              <option value={75}>75 min</option>
              <option value={90}>90 min</option>
              <option value={120}>120 min</option>
            </select>
          </Field>
          <Field label="Calendar slot interval">
            <div className="flex flex-wrap gap-1.5">
              {SLOT_INTERVALS.map((m) => (
                <button
                  key={m}
                  onClick={() => setForm({ ...form, slot_interval_minutes: m })}
                  data-testid={`appt-slot-${m}`}
                  className={[
                    "px-3 py-1 text-xs rounded-md border",
                    form.slot_interval_minutes === m
                      ? "bg-teal-600 text-white border-teal-600 font-semibold"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
                  ].join(" ")}
                >
                  {m}m
                </button>
              ))}
            </div>
          </Field>
          <Field label="Max advance booking">
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max="730" value={form.advance_booking_days}
                onChange={(e) => setForm({ ...form, advance_booking_days: Number(e.target.value) })}
                data-testid="appt-advance-days"
                className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
              />
              <span className="text-sm text-slate-500">days out</span>
            </div>
          </Field>
        </div>
      </Card>

      <Card title="Appointment confirmations" subtitle="Timing rules for patient emails and the front desk call queue. Changes apply on the next cron run.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="First confirmation email">
            <HoursInput
              value={form.confirmation_email_1_hours_before}
              onChange={(value) => setForm({ ...form, confirmation_email_1_hours_before: value })}
              testId="appt-confirm-email-1-hours"
            />
          </Field>
          <Field label="Second confirmation email">
            <HoursInput
              value={form.confirmation_email_2_hours_before}
              onChange={(value) => setForm({ ...form, confirmation_email_2_hours_before: value })}
              testId="appt-confirm-email-2-hours"
            />
          </Field>
          <Field label="Call queue entry">
            <HoursInput
              value={form.call_queue_trigger_hours_before}
              onChange={(value) => setForm({ ...form, call_queue_trigger_hours_before: value })}
              testId="appt-confirm-call-queue-hours"
            />
            <p className="mt-1 text-[11px] text-slate-500">Fires simultaneously with the second email.</p>
          </Field>
        </div>
      </Card>

      <details className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="calendar-ops-advanced">
        <summary className="flex min-h-16 cursor-pointer list-none items-center gap-3 px-5 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
            <SlidersHorizontal className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-slate-900">Advanced calendar operations</span>
            <span className="mt-0.5 block text-xs text-slate-500">Risk labels, queue items, warning times, and front-desk priority order.</span>
          </span>
          <span className="mr-1 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Advanced</span>
          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-5 border-t border-slate-100 p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Late threshold">
              <MinutesInput
                value={calendarOpsForm.thresholds.late_minutes}
                onChange={(value) => setCalendarOpsThreshold("late_minutes", value)}
                testId="calendar-ops-late-minutes"
              />
            </Field>
            <Field label="Waiting threshold">
              <MinutesInput
                value={calendarOpsForm.thresholds.waiting_too_long_minutes}
                onChange={(value) => setCalendarOpsThreshold("waiting_too_long_minutes", value)}
                testId="calendar-ops-waiting-minutes"
              />
            </Field>
            <Field label="Checkout threshold">
              <MinutesInput
                value={calendarOpsForm.thresholds.checkout_waiting_minutes}
                onChange={(value) => setCalendarOpsThreshold("checkout_waiting_minutes", value)}
                testId="calendar-ops-checkout-minutes"
              />
            </Field>
            <Field label="No-show count">
              <NumberInput
                value={calendarOpsForm.thresholds.no_show_count}
                onChange={(value) => setCalendarOpsThreshold("no_show_count", value)}
                testId="calendar-ops-noshow-count"
                suffix="no-shows"
              />
            </Field>
            <Field label="No-show lookback">
              <NumberInput
                value={calendarOpsForm.thresholds.no_show_lookback_months}
                onChange={(value) => setCalendarOpsThreshold("no_show_lookback_months", value)}
                testId="calendar-ops-noshow-months"
                suffix="months"
              />
            </Field>
            <Field label="Unconfirmed lookahead">
              <NumberInput
                value={calendarOpsForm.thresholds.unconfirmed_lookahead_hours}
                onChange={(value) => setCalendarOpsThreshold("unconfirmed_lookahead_hours", value)}
                testId="calendar-ops-unconfirmed-hours"
                suffix="hours"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-700">Risk labels</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CALENDAR_OPS_RISK_LABELS.map((item) => (
                  <ToggleRow
                    key={item.key}
                    label={item.label}
                    checked={calendarOpsForm.risk_labels_enabled[item.key] !== false}
                    onChange={(checked) => setCalendarOpsRisk(item.key, checked)}
                    testId={`calendar-ops-risk-${item.key}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-700">Queue items</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CALENDAR_OPS_QUEUE_ITEMS.map((item) => (
                  <ToggleRow
                    key={item.key}
                    label={item.label}
                    checked={calendarOpsForm.queue_item_types_enabled[item.key] !== false}
                    onChange={(checked) => setCalendarOpsQueueItem(item.key, checked)}
                    testId={`calendar-ops-queue-${item.key}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-slate-700">Queue priority</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {calendarOpsForm.queue_priority.map((key, index) => (
                <label key={`${index}-${key}`} className="block">
                  <span className="mb-1 block text-[11px] font-semibold text-slate-500">Priority {index + 1}</span>
                  <select
                    value={key}
                    onChange={(event) => setCalendarOpsPriority(index, event.target.value)}
                    data-testid={`calendar-ops-priority-${index}`}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
                  >
                    {CALENDAR_OPS_QUEUE_ITEMS.map((item) => (
                      <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>
        </div>
      </details>

      {/* Walk-in policy */}
      <Card title="Walk-in policy" subtitle="What happens when a patient walks in without an appointment.">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-3">
          <input
            type="checkbox" checked={form.walk_in_allowed}
            onChange={(e) => setForm({ ...form, walk_in_allowed: e.target.checked })}
            data-testid="appt-walkin-allowed"
            className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
          />
          Allow walk-ins
        </label>
        {form.walk_in_allowed ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Default chair for walk-ins">
              <select
                value={form.walk_in_default_chair_id || ""}
                onChange={(e) => setForm({ ...form, walk_in_default_chair_id: e.target.value })}
                data-testid="appt-walkin-chair"
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
              >
                <option value="">— None —</option>
                {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Default doctor for walk-ins">
              <select
                value={form.walk_in_default_doctor_id || ""}
                onChange={(e) => setForm({ ...form, walk_in_default_doctor_id: e.target.value })}
                data-testid="appt-walkin-doctor"
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
              >
                <option value="">— None —</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{formatDoctorName(d, "Unnamed")}</option>)}
              </select>
            </Field>
          </div>
        ) : null}
      </Card>

      {/* No-show */}
      <Card title="No-show" subtitle="When the system should auto-flag a missed appointment.">
        <Field label="Auto-mark no-show after">
          <div className="flex items-center gap-2">
            <input
              type="number" min="0" max="240" value={form.no_show_grace_minutes}
              onChange={(e) => setForm({ ...form, no_show_grace_minutes: Number(e.target.value) })}
              data-testid="appt-noshow-grace"
              className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
            />
            <span className="text-sm text-slate-500">minutes past start</span>
          </div>
        </Field>
      </Card>

      {/* Buffer */}
      <Card title="Buffer time" subtitle="Padding between back-to-back appointments.">
        <Field label="Add">
          <div className="flex items-center gap-2">
            <input
              type="number" min="0" max="120" value={form.buffer_minutes}
              onChange={(e) => setForm({ ...form, buffer_minutes: Number(e.target.value) })}
              data-testid="appt-buffer"
              className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
            />
            <span className="text-sm text-slate-500">minutes between appointments</span>
          </div>
        </Field>
      </Card>

      {/* Chair setup time */}
      <Card title="Chair setup time" subtitle="One-time overhead per appointment — numb up, isolation, breakdown, dismiss the patient. Added once when the appointment includes at least one service flagged for setup in Settings → Services.">
        <Field label="Setup time">
          <div className="flex items-center gap-2">
            <input
              type="number" min="0" max="120" value={form.chair_setup_minutes}
              onChange={(e) => setForm({ ...form, chair_setup_minutes: Number(e.target.value) })}
              data-testid="appt-chair-setup"
              className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
            />
            <span className="text-sm text-slate-500">minutes per appointment</span>
          </div>
        </Field>
        <p className="text-[11px] text-slate-500 mt-2 leading-snug">
          Example with the default 15 min: three 1-surface composites in one appointment are 45 min raw work + 15 min setup = 60 min total. A periapical x-ray flagged 'no setup' does not trigger setup by itself.
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers / sub-components
// ---------------------------------------------------------------------
function Card({ title, subtitle, children, tutorialId }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-tutorial-id={tutorialId}>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function HoursInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="1"
        value={value}
        onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
        data-testid={testId}
        className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
      />
      <span className="text-sm text-slate-500">hours before</span>
    </div>
  );
}

function MinutesInput({ value, onChange, testId }) {
  return (
    <NumberInput value={value} onChange={onChange} testId={testId} suffix="minutes" />
  );
}

function NumberInput({ value, onChange, testId, suffix }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="1"
        value={value}
        onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
        data-testid={testId}
        className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded"
      />
      <span className="text-sm text-slate-500">{suffix}</span>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
        className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
      />
      <span>{label}</span>
    </label>
  );
}

function mergeHours(maybe) {
  // Defensively normalize what comes back from the server. Some clinics
  // may have older rows where a day is missing from the JSON; fall back
  // to defaults to keep the form rendering clean.
  // Lunch is optional per day. Only kept if both start and end are present.
  const next = { ...DEFAULT_HOURS };
  if (maybe && typeof maybe === "object") {
    for (const day of DAYS) {
      const v = maybe[day.key];
      if (v && typeof v === "object") {
        const lunchStart = v.lunch?.start || null;
        const lunchEnd   = v.lunch?.end   || null;
        next[day.key] = {
          open:   v.open   ?? DEFAULT_HOURS[day.key].open,
          close:  v.close  ?? DEFAULT_HOURS[day.key].close,
          closed: !!v.closed,
          ...(lunchStart && lunchEnd
            ? { lunch: { start: lunchStart, end: lunchEnd } }
            : {}),
        };
      }
    }
  }
  return next;
}

function normalizeCalendarOpsSettings(maybe) {
  const incoming = maybe && typeof maybe === "object" ? maybe : {};
  const thresholds = incoming.thresholds && typeof incoming.thresholds === "object"
    ? incoming.thresholds
    : {};
  const priority = Array.isArray(incoming.queue_priority) && incoming.queue_priority.length > 0
    ? incoming.queue_priority
    : DEFAULT_CALENDAR_OPS_SETTINGS.queue_priority;
  return {
    risk_labels_enabled: {
      ...DEFAULT_CALENDAR_OPS_SETTINGS.risk_labels_enabled,
      ...(incoming.risk_labels_enabled || {}),
    },
    queue_item_types_enabled: {
      ...DEFAULT_CALENDAR_OPS_SETTINGS.queue_item_types_enabled,
      ...(incoming.queue_item_types_enabled || {}),
    },
    queue_priority: priority.filter(Boolean),
    thresholds: {
      ...DEFAULT_CALENDAR_OPS_SETTINGS.thresholds,
      ...thresholds,
    },
    queue_scope: incoming.queue_scope || DEFAULT_CALENDAR_OPS_SETTINGS.queue_scope,
  };
}
